import React, { useRef, useState } from "react";
import * as axios from "axios";
import { Select, TextInput } from "@opendate-app/setlist";
import { useMembershipProductNewContext } from "./MembershipProductNewContainer";
import { toastError } from "../../shared/toastHelper";

const POLICY_OPTIONS = [
  { value: "prorate", label: "Prorated difference" },
  { value: "window", label: "Upgrade window" },
];

const MIN_DAYS = 1;
const MAX_DAYS = 365;

const MembershipTeamRules = ({ disabled = false }) => {
  const { csrfToken, membershipUpgradePolicy, membershipUpgradeWindowDays, membershipSettingsPath } = useMembershipProductNewContext();
  const [saved, setSaved] = useState({ policy: membershipUpgradePolicy, days: membershipUpgradeWindowDays });
  // Saves are queued, so a revert must read what was saved last, not what this render saw.
  const savedRef = useRef(saved);
  savedRef.current = saved;
  const [policy, setPolicy] = useState(membershipUpgradePolicy);
  const [days, setDays] = useState(String(membershipUpgradeWindowDays));

  const queue = useRef(Promise.resolve());

  // Each control sends and records only its own field, and saves go out one at a
  // time in order, so a slow earlier request can't land after a newer one.
  const save = (team, savedField, revert) => {
    queue.current = queue.current.then(async () => {
      try {
        const { data } = await axios.patch(membershipSettingsPath, { team }, { headers: { "X-CSRF-TOKEN": csrfToken } });
        savedRef.current = { ...savedRef.current, ...savedField(data) };
        setSaved(savedRef.current);
      } catch (error) {
        toastError(error.response?.data?.errors?.[0] || "Couldn't save upgrade pricing.");
        revert();
      }
    });
  };

  const validDays = (text) => text.trim() !== "" && Number.isInteger(Number(text)) && Number(text) >= MIN_DAYS && Number(text) <= MAX_DAYS;

  const saveDays = () => {
    const value = Number(days);
    if (!validDays(days)) {
      setDays(String(saved.days));
      return;
    }
    if (value !== saved.days) save({ membership_upgrade_window_days: value }, (data) => ({ days: data.membershipUpgradeWindowDays }), () => setDays(String(savedRef.current.days)));
  };

  const hint =
    policy === "window"
      ? `Within ${validDays(days) ? Number(days) : saved.days} days of joining or renewing, members pay just the difference. After that, an upgrade is an early renewal at full price and adds a year.`
      : "Members pay the difference for the time left in their billing period.";

  return (
    <div className="membership-team-rules" data-testid="membership-team-rules">
      <span className="membership-eyebrow">Team rules · all memberships</span>
      <Select
        id="membership-upgrade-policy"
        label="Upgrade pricing"
        options={POLICY_OPTIONS}
        disabled={disabled}
        value={policy}
        onChange={(value) => {
          if (!value || value === policy) return;
          setPolicy(value);
          save({ membership_upgrade_policy: value }, (data) => ({ policy: data.membershipUpgradePolicy }), () => setPolicy(savedRef.current.policy));
        }}
      />
      {policy === "window" ? (
        <TextInput
          id="membership-upgrade-window"
          label="Window"
          type="number"
          min={MIN_DAYS}
          max={MAX_DAYS}
          trailingText="days"
          disabled={disabled}
          value={days}
          onChange={(e) => setDays(e.target.value)}
          onBlur={saveDays}
        />
      ) : null}
      <p className="membership-hint">{hint} Higher price means upgrade. Downgrades start at renewal.</p>
    </div>
  );
};

export default MembershipTeamRules;
